<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { fetchWrapper } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
import { getBaseUrl } from '@/helpers/fetch-wrapper'
import { useAuthStore } from '@/stores/auth.store'
import { useRouter } from 'vue-router'

const router = useRouter()
const { user } = useAuthStore()
const debug = ref(false)

const { t } = useI18n()
const audiomanager = inject('audiomanager')

const props = defineProps({
  gameState: {
    type: Object,
    required: true
  },
  enable_audio: {
    type: Boolean,
  },
})


const emit = defineEmits(['game-action', 'toggle-audio'])

// Local UI state
const submittedRating = ref(false)
const userInput = ref('')
const hasAnswered = ref(false)
const showAnswer = ref(false)
const selectedRating = ref(null)
const isDrawing = ref(false)
const canvasRef = ref(null)
const ctx = ref(null)
const showControls = ref(false)

let savedCanvasData = null // Variable to store the canvas content

// Computed properties
const currentConcept = computed(() => props.gameState.current_concept)

const showTextInput = computed(() => {
  if (props.gameState.game_type === 'free-translation-writing') {
    return false
  }
  else {
    return true
  }
})

const questionWordDisplay = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  
  if (showAnswer.value) {
    return props.gameState.direction === 'native-to-learning'
      ? concept.native.text
      : concept.learning.annotated.annotation
  }
  else {
    return props.gameState.direction === 'native-to-learning'
      ? concept.native.text
      : concept.learning.text
  }
})

function chatWithTutor() {
  showControls.value = false
  emit('chat-with-tutor')
}

function showExampleSentences() {
  showControls.value = false
  emit('example-sentences')
}

const correctAnswer = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  if (props.gameState.game_type === 'free-translation-writing') {
    return concept.learning.text
  }

  if (showAnswer.value && props.gameState.direction === 'native-to-learning') {
    return concept.learning.annotated?.annotation || concept.learning.text
  }

  return props.gameState.direction === 'native-to-learning'
    ? concept.learning.text
    : concept.native.text
})

// Check if undo is available
const canUndo = computed(() => !!props.gameState.last_flashcard_logID)

// Methods
// just for debugging
function hideAnswer() {
  showAnswer.value = false
}
function revealAnswer() {
  showAnswer.value = true

  // Play native language audio
  if (props.enable_audio) {
    playLearningAudio()
  }
}

function getAudioUrl() {
  const concept = currentConcept.value
  if (!concept?.learning) return ''
  return getBaseUrl(`/tts/?lang=${concept.learning.language}&text=${concept.learning.text}&token=${user.token}`)
}
audiomanager.preload(getAudioUrl())

function playLearningAudio() {
  const concept = currentConcept.value
  if (!concept?.learning) return
  const url = getAudioUrl()
  audiomanager.setPlaybackSpeed(1.0)
  audiomanager.play(url)
}

function playSlowAudio() {
  const concept = currentConcept.value
  if (!concept?.learning) return
  const url = getAudioUrl()
  audiomanager.setPlaybackSpeed(0.5)
  audiomanager.play(url)
}

function submitRating(rating) {
  selectedRating.value = rating

  // Emit game action with the selected rating
  emit('game-action', {
    text: userInput.value,
    expected: correctAnswer.value,
    rating: rating,
    correct: rating > 1,
  })
  submittedRating.value = true
}

async function toggleAudio() {
  emit('toggle-audio')
}

async function undoLastReview() {
  if (!canUndo.value) return

  try {
    const response = await fetchWrapper.post(`/flashcard/undo/`, {
      flashcard_logID: props.gameState.last_flashcard_logID,
    })
    console.log('Undo successful:', response)
    emit('reload')
  } catch (error) {
    console.error('Error undoing last review:', error)
  }
}

function resetGame() {
  userInput.value = ''
  hasAnswered.value = false
  showAnswer.value = false
  selectedRating.value = null
  clearCanvas()
}

// Canvas-related methods
function startDrawing(event) {
  if (showAnswer.value) return
  event.preventDefault() // Prevent scrolling
  isDrawing.value = true
  const rect = canvasRef.value.getBoundingClientRect()
  const clientX = event.touches ? event.touches[0].clientX : event.clientX
  const clientY = event.touches ? event.touches[0].clientY : event.clientY
  ctx.value.beginPath()
  ctx.value.moveTo(clientX - rect.left, clientY - rect.top)
}

function draw(event) {
  if (showAnswer.value) return
  if (!isDrawing.value) return
  event.preventDefault() // Prevent scrolling
  const rect = canvasRef.value.getBoundingClientRect()
  const clientX = event.touches ? event.touches[0].clientX : event.clientX
  const clientY = event.touches ? event.touches[0].clientY : event.clientY
  ctx.value.lineTo(clientX - rect.left, clientY - rect.top)
  ctx.value.stroke()
}

function stopDrawing() {
  if (showAnswer.value) return
  isDrawing.value = false
}

function clearCanvas() {
  if (showAnswer.value) return
  if (ctx.value) {
    ctx.value.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  }
  showControls.value = false
}

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) {
    console.log('must not be a canvas, not adding bindings')
    return
  }
  ctx.value = canvas.getContext('2d')
  ctx.value.lineWidth = 2
  ctx.value.strokeStyle = 'black'

  // Add touch event listeners
  canvas.addEventListener('touchstart', startDrawing)
  canvas.addEventListener('touchmove', draw)
  canvas.addEventListener('touchend', stopDrawing)
  canvas.addEventListener('touchcancel', stopDrawing)

  // Add mouse event listeners (already present in the template)
})

</script>

<template>
  <div class="ft-game-container">
    <div class="game-top">

      <div class="word-to-translate mb-2">
        <div class="me-0" style="float: right">
          <button
            type="button"
            class="btn-grear"
            @click="showControls = !showControls">
            <i :class="showControls ? 'bi bi-gear' : 'bi bi-gear'"></i>
          </button>
          <div v-if="showControls" class="popout-menu">
            <ul class="text-start">
              <li @click="chatWithTutor()">
                <i class="bi bi-chat-dots me-1"></i>
                {{ t('course.chat-with-tutor') }}
              </li>
              <li @click="showExampleSentences()">
                <i class="bi bi-translate me-1"></i>
                {{ t('curriculum.example-sentences') }}
              </li>
              <li v-if="props.gameState.vocab_entry_id" @click="router.push({ name: 'EditVocab', params: { id: props.gameState.vocab_entry_id } })" >
                <i class="bi bi-pencil-square me-1"></i>
                {{ t('curriculum.edit-vocab') }}
              </li>
              <li v-if="canUndo" class="d-flex align-items-center" @click="undoLastReview">
                <i class="bi bi-arrow-counterclockwise me-1"></i>
                {{ t('game.undo') }}
              </li>
              <li @click="toggleAudio" >
                <i :class="enable_audio ? 'bi bi-volume-up-fill me-1' : 'bi bi-volume-mute-fill me-1'"></i>
                {{ enable_audio ? t('game.audio-on') : t('game.audio-off') }}
              </li>
              <li @click="playLearningAudio" >
                <i class="bi bi-play-circle me-1"></i>
                {{ t('curriculum.play-again') }}
              </li>
              <li @click="playSlowAudio" >
                <i class="bi bi-play-circle me-1"></i>
                {{ t('curriculum.play-slow') }}
              </li>
              <li v-if="debug" @click="hideAnswer">
                <i class="bi bi-eye-slash me-1"></i>
                Hide answer
              </li>
              <li v-if="showControls && !showTextInput" @click="clearCanvas">
                <i class="bi bi-eraser me-1"></i>
                Erase drawing
              </li>
            </ul>
          </div>
        </div>
        <h2 class="mb-2" style="" v-html="questionWordDisplay"></h2>
      </div>

      <div class="feedback mb-4" v-if="showAnswer && showTextInput">
        <p class="text-info" v-if="userInput == correctAnswer">
          <span class="correct-answer" v-html="correctAnswer"></span>
        </p>
        <div v-else-if="userInput && userInput != correctAnswer">
          <p class="text-warning">
            {{ t('game.your-answer') }}: <span class="correct-answer" v-html="userInput"></span>
          </p>
          <p class="text-info">
            {{ t('game.expected-answer') }}: <span class="correct-answer" v-html="correctAnswer"></span>
          </p>
        </div>
        <p class="text-info" v-else-if="!userInput">
          <span class="correct-answer" v-html="correctAnswer"></span>
        </p>
      </div>

      <div class="input-container mb-4" v-if="!showAnswer && showTextInput">
        <input
          type="text"
          class="form-control"
          v-model="userInput"
          :readonly="showAnswer"
          :placeholder="t('game.type-your-answer-here')"
          @keyup.enter="revealAnswer"
        />
      </div>
      <div v-if="!showTextInput" class="row">

        <!-- Drawing Canvas -->
        <div
          v-disable-back-swipe
          class="canvas-container mb-4"
          :class="showAnswer ? 'col-6' : 'col-12'"
        >
          <canvas
            width=350
            height=350
            ref="canvasRef"
            class="drawing-canvas"
            :class="showAnswer ? 'small-canvas' : ''"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
          ></canvas>
        </div>
        <div class="col-6" v-if="showAnswer">
          {{ t('game.correct-answer') }}:<br> <span class="big-answer" v-html="correctAnswer"></span>
        </div>
      </div>

    </div>
    <div class="actions">
      <button type="button" v-if="!showAnswer" class="btn btn-primary btn-primary-cs" @click="revealAnswer" >
        {{ t('game.check-answer') }}
      </button>

      <div v-else><!-- show rating response -->
        <div class="rating-buttons" v-if="!selectedRating">

          <button type="button" class="btn btn-outline-danger" @click="submitRating(1)" :disabled="!!selectedRating" >
            {{ t('cards.incorrect') }}
          </button>
          <button type="button" class="btn btn-outline-warning" @click="submitRating(2)" :disabled="!!selectedRating" >
            {{ t('cards.hard') }}
          </button>
          <button type="button" class="btn btn-outline-success" @click="submitRating(3)" :disabled="!!selectedRating" >
            {{ t('cards.correct') }}
          </button>
          <button type="button" class="btn btn-outline-primary" @click="submitRating(4)" :disabled="!!selectedRating" >
            {{ t('cards.easy') }}
          </button>
        </div>
      </div><!-- end show rating response-->
    </div>
  </div>
</template>

<style scoped>
.ft-game-container {
  display: flex;
  flex-direction: column;
  justify-content: top;
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 1rem;
  height: 100%;
}

.word-to-translate {
  background-color: #f8f9fa;
  padding: 0.3rem;
  border-radius: 8px;
  overflow: hidden;
}
.feedback {
  background-color: #e9ecef;
  padding: 0.5rem;
  border-radius: 8px;
}

.input-container input {
  font-size: 1.1rem;
  padding: 0.5rem;
}

.feedback p {
  font-size: 1.2rem;
}

.actions button {
  font-size: 1rem;
}

.rating-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center; /* Center the buttons horizontally */
}

.canvas-container {
  text-align: center;
}

.drawing-canvas {
  border: 1px solid #ccc;
  display: block;
  margin: 0 auto;
}

.small-canvas {
  width: 200px;
  height: 200px;
}
.big-answer {
  font-size: 6rem;
  font-weight: bold;
}
/* Styles for the popout menu */
.popout-menu {
    position: fixed;
    z-index: 1000;
    background: white;
    border: 1px solid #ccc;
    border-radius: 6px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transform: translateX(-80%);
    width: 250px;
}

.popout-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.popout-menu li {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid #ccc;
  font-size:14px;
  font-weight:500;
}


.popout-menu li:hover {
    background-color: #f1f1f1;
}
.game-top {
  flex: 1;
  align-items: center;
}
.actions {
  justify-content: center;
  margin-top: 1rem;
/*  background-color: pink;*/
}
.btn-grear{
  background: transparent;
  padding: 5px 10px;
}
.bi {
  color: black;
}
.correct-answer {
  font-weight: bold;
  font-size: 2rem;
  color: #007bff;
}
</style>